import { useEffect, useState, type ReactNode } from 'react';
import { useMediaUrl } from '../../chat/media';
import { fanslyMedia } from '../../crm/client';
import { FANSLY_CDN } from '../../crm/client/cdnMedia';

function initial(name: string | null | undefined): string {
  return (String(name || '').trim()[0] || '?').toUpperCase();
}

/** A round avatar: the picture when there is one (and it loads), else the initial. */
type AvatarProps = { src?: string | null; name: string | null | undefined; size: 16 | 18 | 20 | 28 | 30 | 36 | 52; ring?: string; className?: string; children?: ReactNode };

export function Avatar({ src, name, size, ring, className = '', children }: AvatarProps) {
  const [failed, setFailed] = useState<string | null>(null);
  const show = !!src && failed !== src;
  return (
    <span className={`ws-ini ws-ini--${size} ${className}`} style={ring ? { boxShadow: ring } : undefined}>
      {show ? <img src={src!} alt="" loading="lazy" draggable={false} onError={() => setFailed(src!)} /> : initial(name)}
      {children}
    </span>
  );
}

function isFanslyUrl(src: string | null | undefined): boolean {
  try {
    const u = new URL(String(src || ''));
    return FANSLY_CDN.isCdnHost(u.hostname, u.pathname);
  } catch {
    return false;
  }
}

/** A Fansly link: IP-locked ones load through the CRM's Fansly media proxy (ADR 0045). */
function useFanslyAvatar(accountId: string, src: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!src) {
      setUrl(null);
      return;
    }
    const abort = new AbortController();
    fanslyMedia.load(accountId, src, abort.signal).then(
      (m) => !abort.signal.aborted && setUrl(m.kind === 'blocked' ? null : m.src),
      () => !abort.signal.aborted && setUrl(null),
    );
    return () => abort.abort();
  }, [accountId, src]);
  return url;
}

/** A fan's avatar: links locked to the Account's proxy IP (OnlyFans, chat/media.ts; Fansly) load through the CRM. */
export function FanAvatar({ accountId, src, ...rest }: AvatarProps & { accountId: string }) {
  const fansly = isFanslyUrl(src);
  const media = useMediaUrl(accountId, fansly ? null : src || null);
  const fanslyUrl = useFanslyAvatar(accountId, fansly ? src || null : null);
  return <Avatar {...rest} src={fansly ? fanslyUrl : media.url} />;
}
